/**
 * Layout table proxy — same row shape as fetchUserSite layout* / CustomerSiteLayout* tables.
 *
 * Editor draft → layout table proxy (same shape as fetchUserSite layout*).
 *
 * Round bins: group-local center + catalog diameter (BTS), matching Site 3D spacing.
 * Flat stices: group-local center; layoutSides bin-local feet on the stice bin, built from
 * surrounding round bins (catalog-radius perimeter arcs) + user walls/arcs — then
 * sideToGroupAbsolute adds stice layoutBin x/z for drawSite(), like CustomerSiteLayoutSide rows.
 */
import * as global from "../data/global";
import { createApiEndpoint } from "../api/createApiEndpoint.js";
import { isHumiditySensor } from "./siteSetupDefaults.js";
import {
    chainLayoutSidesLocal,
    chainTolForLayoutSideWorld,
    orderLayoutSideRefsByEndpoints,
} from "./siteLayoutStice.js";
import {
    centerPolygonFtAtOrigin,
    polyFtFromOrderedSides,
} from "../3ds/bin/stice/layoutPreviewShape.js";

export const sameBinDataId = (a, b) => {
    if (a === undefined || a === null || b === undefined || b === null) return false;
    const na = parseInt(String(a), 10);
    const nb = parseInt(String(b), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
    return String(a) === String(b);
};

/** Temputech BinId at/above this range are layout-only placeholders (not dbo.Bin). */
export const LAYOUT_PLACEHOLDER_BIN_ID_BASE = 9_000_000;
export const LAYOUT_PLACEHOLDER_BIN_ID_MAX =
    LAYOUT_PLACEHOLDER_BIN_ID_BASE + 99_999;

export const isLayoutPlaceholderBinId = (id) => {
    const n = parseInt(id, 10);
    return (
        Number.isFinite(n) &&
        n >= LAYOUT_PLACEHOLDER_BIN_ID_BASE &&
        n <= LAYOUT_PLACEHOLDER_BIN_ID_MAX
    );
};

/** Stable positive BinId for unassigned editor areas so SaveCustomerSiteLayout persists every click. */
export const layoutPlaceholderBinId = (areaIndex) =>
    LAYOUT_PLACEHOLDER_BIN_ID_BASE +
    Math.max(0, parseInt(areaIndex, 10) || 0) +
    1;

/** Catalog assignment from a layout row, or null when placeholder / unset. */
export const catalogBinIdFromLayoutBinId = (layoutBinId) => {
    if (isLayoutPlaceholderBinId(layoutBinId)) return null;
    const n = parseInt(layoutBinId, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Keys for matching layout bins across proxy rebuilds (customerSiteLayoutBinId changes every save).
 * Prefer binName so editor placeholders (9M+) still match API cables on stices 33/34/35.
 */
/** "33", "Bin 33", "bin #33" → "33" for stice ↔ BTS matching. */
export const normalizeLayoutBinLabel = (name) => {
    if (name == null || name === "") return "";
    const s = String(name).trim();
    const num = s.match(/^(?:bin\s*#?\s*)?(\d+)$/i);
    if (num) return num[1];
    return s.toLowerCase();
};

export const layoutBinNamesMatch = (a, b) => {
    const na = normalizeLayoutBinLabel(a);
    const nb = normalizeLayoutBinLabel(b);
    return na.length > 0 && na === nb;
};

export const layoutBinMatchKeys = (lb) => {
    if (!lb) return [];
    const keys = [];
    const name =
        lb.binName != null
            ? String(lb.binName).trim()
            : lb.bin?.binName != null
              ? String(lb.bin.binName).trim()
              : "";
    if (name) keys.push(`name:${name}`);
    const norm = normalizeLayoutBinLabel(name);
    if (norm) keys.push(`label:${norm}`);
    const id = parseInt(lb.binId ?? lb.bin?.binDataId ?? lb.bin?.binId, 10);
    if (Number.isFinite(id) && id > 0 && !isLayoutPlaceholderBinId(id)) {
        keys.push(`id:${id}`);
    }
    return keys;
};

export const layoutBinMatchKey = (lb) => layoutBinMatchKeys(lb)[0] ?? null;

/** Layout row → BTS bin (for cableList on stices 33/34/35). */
export const findSiteBinForLayoutBin = (site, layoutBin) => {
    if (!site?.bins?.length || !layoutBin) return null;
    const dataId = layoutBin.binId;
    const name = layoutBin.binName;
    return (
        site.bins.find((b) => {
            const bId = b.binDataId ?? b.binId;
            if (
                dataId != null &&
                !isLayoutPlaceholderBinId(dataId) &&
                sameBinDataId(bId, dataId)
            ) {
                return true;
            }
            return (
                name != null &&
                b.binName != null &&
                layoutBinNamesMatch(b.binName, name)
            );
        }) ?? null
    );
};

/** Cable name prefix before trailing number: "B-12" → "B", "Small House-3" → "Small House". */
export const layoutCableNamePrefix = (cable) => {
    const raw = String(cable?.cableName || cable?.name || "").trim();
    if (!raw) return "";
    return raw.replace(/[-\s]*\d+\s*$/, "").trim();
};

/**
 * True / false / null when a layout cable clearly belongs / does not belong / is unclear
 * for this layout bin (Crittenden B-* vs S-* vs "Big House" / "Small House").
 */
export const layoutCableBelongsToLayoutBinByName = (layoutCable, layoutBin, site = null) => {
    if (!layoutCable || !layoutBin) return null;
    if (layoutBinNamesMatch(layoutCable.binName, layoutBin.binName)) return true;

    const prefix = layoutCableNamePrefix(layoutCable);
    if (!prefix) return null;
    const pn = normalizeLayoutBinLabel(prefix);
    const bn = normalizeLayoutBinLabel(layoutBin.binName);
    if (!pn || !bn) return null;
    if (pn === bn || bn.startsWith(pn) || pn.startsWith(bn)) return true;

    // Prefix uniquely matches a different layout bin → not ours.
    const others = (site?.layoutBins || []).filter(
        (lb) =>
            lb.customerSiteLayoutBinId !== layoutBin.customerSiteLayoutBinId &&
            !lb.isRound
    );
    for (const lb of others) {
        const obn = normalizeLayoutBinLabel(lb.binName);
        if (obn && (obn.startsWith(pn) || pn.startsWith(obn) || pn === obn)) {
            return false;
        }
    }
    return null;
};

/** Cables for a layout bin (id, catalog id, or label e.g. stice "33"). */
export const layoutCablesForLayoutBin = (site, layoutBin) => {
    if (!site?.layoutCables?.length || !layoutBin) return [];
    const linkedSiteBin = findSiteBinForLayoutBin(site, layoutBin);
    const catalogId =
        linkedSiteBin?.binDataId ??
        linkedSiteBin?.binId ??
        (!isLayoutPlaceholderBinId(layoutBin.binId) ? layoutBin.binId : null);
    const layoutBinId = layoutBin.customerSiteLayoutBinId;

    return site.layoutCables.filter((lc) => {
        const byName = layoutCableBelongsToLayoutBinByName(lc, layoutBin, site);
        if (byName === false) return false;

        if (
            lc.customerSiteLayoutBinId != null &&
            layoutBinId != null &&
            lc.customerSiteLayoutBinId === layoutBinId
        ) {
            return true;
        }

        // Explicitly tagged to another layout bin on this site.
        if (
            lc.customerSiteLayoutBinId != null &&
            (site.layoutBins || []).some(
                (lb) =>
                    lb.customerSiteLayoutBinId === lc.customerSiteLayoutBinId &&
                    lb.customerSiteLayoutBinId !== layoutBinId
            )
        ) {
            return false;
        }

        if (byName === true) return true;

        if (layoutBinNamesMatch(lc.binName, layoutBin.binName)) {
            return true;
        }
        const lbId = layoutBin.binId;
        if (
            lbId != null &&
            !isLayoutPlaceholderBinId(lbId) &&
            (sameBinDataId(lc.binId, lbId) ||
                (lc.binDataId != null && sameBinDataId(lc.binDataId, lbId)))
        ) {
            return true;
        }
        if (
            catalogId != null &&
            !isLayoutPlaceholderBinId(catalogId) &&
            (sameBinDataId(lc.binId, catalogId) ||
                (lc.binDataId != null && sameBinDataId(lc.binDataId, catalogId)))
        ) {
            // Shared catalog id across multiple flats: only accept when name agrees or is unknown.
            const multiFlat =
                (site.layoutBins || []).filter((lb) => !lb.isRound).length > 1;
            if (multiFlat && byName === null) {
                const prefix = layoutCableNamePrefix(lc);
                if (prefix) return false;
            }
            return true;
        }
        return false;
    });
};

/** BTS bin → layout row (round or flat/stice). */
export const findLayoutBinForSiteBin = (site, siteBin) => {
    if (!site?.layoutBins?.length || !siteBin) return null;
    const dataId = siteBin.binDataId ?? siteBin.binId;
    const name =
        siteBin.binName != null ? String(siteBin.binName).trim() : "";

    const matches = site.layoutBins.filter((lb) => {
        if (lb.isRound) {
            return (
                (dataId != null && sameBinDataId(lb.binId, dataId)) ||
                (name &&
                    lb.binName != null &&
                    layoutBinNamesMatch(lb.binName, name))
            );
        }
        return (
            (dataId != null &&
                sameBinDataId(lb.binId, dataId) &&
                !isLayoutPlaceholderBinId(lb.binId)) ||
            (name &&
                lb.binName != null &&
                layoutBinNamesMatch(lb.binName, name))
        );
    });
    if (!matches.length) return null;

    const sticeMatches = matches.filter((lb) => !lb.isRound);
    const catalogLooksStice = /flat|house/i.test(String(siteBin.type || ""));

    const exactId = (rows) =>
        rows.find(
            (lb) =>
                dataId != null &&
                !isLayoutPlaceholderBinId(lb.binId) &&
                sameBinDataId(lb.binId, dataId)
        );

    if (catalogLooksStice && sticeMatches.length) {
        return exactId(sticeMatches) ?? sticeMatches[0];
    }

    const idHit = exactId(matches);
    // ADM interstice 11: a round silo may carry the same dbo.Bin id; prefer the named stice.
    if (idHit?.isRound && sticeMatches.length) {
        const namedStice = name
            ? sticeMatches.find((lb) => layoutBinNamesMatch(lb.binName, name))
            : sticeMatches[0];
        if (namedStice) return namedStice;
    }
    if (idHit) return idHit;

    if (sticeMatches.length === 1 && matches.some((lb) => lb.isRound)) {
        return sticeMatches[0];
    }

    return (
        matches.find((lb) => !isLayoutPlaceholderBinId(lb.binId)) ?? matches[0]
    );
};

/** BTS / dropdown id for a layout row (Temputech layoutBin.binId often differs from dbo.Bin). */
export const catalogBinDataIdForLayoutBin = (site, layoutBin) => {
    if (!layoutBin) return null;
    const siteBin = findSiteBinForLayoutBin(site, layoutBin);
    const id = siteBin?.binDataId ?? siteBin?.binId;
    if (id != null && !isLayoutPlaceholderBinId(id)) return id;
    if (
        layoutBin.binId != null &&
        !isLayoutPlaceholderBinId(layoutBin.binId)
    ) {
        return layoutBin.binId;
    }
    return null;
};

/** BTS / dropdown id for a 3D binsRef group (binDataId there is layoutBin.binId). */
export const catalogBinDataIdForBinRef = (site, binRef) => {
    if (!site || !binRef) return null;
    const layoutBin =
        binRef.layoutBin ||
        (site.layoutBins || []).find((lb) =>
            sameBinDataId(lb.customerSiteLayoutBinId, binRef.binId)
        );
    const fromLayout = catalogBinDataIdForLayoutBin(
        site,
        layoutBin || {
            binId: binRef.binDataId,
            binName: binRef.name || binRef.binName,
        }
    );
    if (fromLayout != null) return fromLayout;
    const byName = (site.bins || []).find((b) =>
        layoutBinNamesMatch(b.binName, binRef.name || binRef.binName)
    );
    return byName?.binDataId ?? byName?.binId ?? null;
};

/** True when this layout bin is the one the Bin / Site 3D dropdown currently names. */
export const layoutBinMatchesDropdownId = (site, layoutBin, dropdownId) => {
    if (!layoutBin || dropdownId == null || dropdownId === "") return false;
    if (dropdownId === 0 || dropdownId === "0") return false;

    const siteBin = (site?.bins || []).find(
        (b) =>
            sameBinDataId(b.binDataId, dropdownId) ||
            sameBinDataId(b.binId, dropdownId)
    );
    const selectedLayout = findLayoutBinForSiteBin(
        site,
        siteBin || {
            binDataId: dropdownId,
            binId: dropdownId,
            binName: siteBin?.binName,
            type: siteBin?.type,
        }
    );
    if (!selectedLayout) {
        return sameBinDataId(layoutBin.binId, dropdownId);
    }
    return sameBinDataId(
        selectedLayout.customerSiteLayoutBinId,
        layoutBin.customerSiteLayoutBinId
    );
};

/** binId for save payload: real catalog id, or placeholder when area is unassigned. */
export const resolveLayoutBinIdForSave = (catalogBinDataId, areaIndex, forSave) => {
    const catalog = parseInt(catalogBinDataId, 10);
    if (Number.isFinite(catalog) && catalog > 0 && !isLayoutPlaceholderBinId(catalog)) {
        return catalog;
    }
    if (!forSave) return 0;
    return layoutPlaceholderBinId(areaIndex);
};

/**
 * True when side endpoints are stice-centered feet (not yet offset by layoutBin x/z).
 * Large flat pockets can have 80+ ft local edges; the old half-footprint test skipped
 * promotion and drawSite() built giant wall meshes (ADM stice 11).
 */
export const layoutSideLooksBinLocal = (side, layoutBin) => {
    const cx = layoutBin?.x ?? 0;
    const cz = layoutBin?.z ?? 0;
    const span =
        Math.max(layoutBin?.width || 0, layoutBin?.length || 0, 30) * 1.75;
    const pts = [
        [side?.x1, side?.z1],
        [side?.x2, side?.z2],
        [side?.curveX, side?.curveZ],
    ];
    let sawFinite = false;
    for (const [x, z] of pts) {
        if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
        sawFinite = true;
        const nearBinOrigin = Math.hypot(x, z) <= span;
        const nearGroupAnchor = Math.hypot(x - cx, z - cz) <= span;
        if (nearGroupAnchor && !nearBinOrigin) return false;
    }
    return sawFinite;
};

/** fetchUserSite / drawSite: layoutSides.x1/z1 are group-relative absolute (bin.x + local). */
export const promoteLayoutSidesForDrawSite = (site) => {
    if (!site?.layoutSides?.length || !site?.layoutBins?.length) return site;
    const binById = new Map(
        site.layoutBins.map((lb) => [lb.customerSiteLayoutBinId, lb])
    );
    // All of a bin's walls share one frame, but layoutSideLooksBinLocal is a per-endpoint
    // distance heuristic: promoting only the walls that trip it leaves the perimeter half
    // offset and half not, so the footprint loop can no longer be chained.
    const promoteBinId = new Map();
    site.layoutSides.forEach((side) => {
        const binId = side.customerSiteLayoutBinId;
        const lb = binById.get(binId);
        if (!lb || lb.isRound) {
            promoteBinId.set(binId, false);
            return;
        }
        const binLocal = layoutSideLooksBinLocal(side, lb);
        promoteBinId.set(binId, (promoteBinId.get(binId) ?? true) && binLocal);
    });
    site.layoutSides = site.layoutSides.map((side) => {
        if (!promoteBinId.get(side.customerSiteLayoutBinId)) return side;
        const abs = sideToGroupAbsolute(
            side,
            binById.get(side.customerSiteLayoutBinId)
        );
        clearSideDrawSiteFields(abs);
        return abs;
    });
    return site;
};

/** @deprecated Use promoteLayoutSidesForDrawSite */
export const ensureLayoutSidesGroupAbsoluteForDrawSite = promoteLayoutSidesForDrawSite;

const LAYOUT_DRAFT_SITE3D_KEY = (siteGuid) =>
    `gw-layout-draft-site3d:${siteGuid ?? ""}`;
const LAYOUT_PROXY_SITE3D_KEY = (siteGuid) =>
    `gw-layout-proxy-site3d:${siteGuid ?? ""}`;

const readJsonStorage = (storage, key) => {
    try {
        const raw = storage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeJsonStorage = (storage, key, value) => {
    try {
        storage.setItem(key, JSON.stringify(value));
        return true;
    } catch {
        return false;
    }
};

/** Last editor buildDraft so Site 3D can match Preview (not a lossy API round-trip). */
export const saveLayoutDraftForSite3d = (siteGuid, draft) => {
    if (!siteGuid || !draft?.groups?.length) return;
    const payload = { ...draft, savedAt: Date.now() };
    writeJsonStorage(sessionStorage, LAYOUT_DRAFT_SITE3D_KEY(siteGuid), payload);
    writeJsonStorage(localStorage, LAYOUT_DRAFT_SITE3D_KEY(siteGuid), payload);
};

export const loadLayoutDraftForSite3d = (siteGuid) => {
    if (!siteGuid) return null;
    const draft =
        readJsonStorage(sessionStorage, LAYOUT_DRAFT_SITE3D_KEY(siteGuid)) ||
        readJsonStorage(localStorage, LAYOUT_DRAFT_SITE3D_KEY(siteGuid));
    return draft?.groups?.length ? draft : null;
};

/**
 * Exact layout-table proxy from last Save layout (Preview geometry).
 * fetchUserSite often returns different bin/side coords (~35 ft drift on ADM).
 */
export const saveLayoutProxyForSite3d = (siteGuid, proxy, cableState) => {
    if (!siteGuid || !proxy?.layoutBins?.length) return;
    const payload = {
        layout: proxy.layout,
        layoutGroups: proxy.layoutGroups,
        layoutBins: proxy.layoutBins,
        layoutSides: proxy.layoutSides,
        layoutCables: cableState?.layoutCables ?? proxy.layoutCables ?? [],
        layoutSensors: cableState?.layoutSensors ?? proxy.layoutSensors ?? [],
        savedAt: Date.now(),
    };
    writeJsonStorage(sessionStorage, LAYOUT_PROXY_SITE3D_KEY(siteGuid), payload);
    writeJsonStorage(localStorage, LAYOUT_PROXY_SITE3D_KEY(siteGuid), payload);
};

export const loadLayoutProxyForSite3d = (siteGuid) => {
    if (!siteGuid) return null;
    const proxy =
        readJsonStorage(sessionStorage, LAYOUT_PROXY_SITE3D_KEY(siteGuid)) ||
        readJsonStorage(localStorage, LAYOUT_PROXY_SITE3D_KEY(siteGuid));
    return proxy?.layoutBins?.length ? proxy : null;
};

/**
 * Site has an exact layoutProxy committed for Site 3D (DB LayoutProxyJson / EditorDraftJson).
 */
export const siteHasCommittedSite3dLayoutProxy = (site) =>
    !!(
        site?.hasCommittedSite3dLayoutProxy ||
        site?.persistedLayoutProxyFromEditor?.layoutBins?.length
    );

/** Parse layoutProxy from fetchUserSite layoutEditorProxy / layoutProxyJson field. */
export const extractLayoutProxyFromSiteFetch = (siteData) => {
    if (!siteData || typeof siteData !== "object") return null;
    return (
        extractLayoutProxyFromEditorDraft(siteData.layoutProxyJson) ||
        extractLayoutProxyFromEditorDraft(siteData.layoutEditorProxy) ||
        extractLayoutProxyFromEditorDraft(siteData.LayoutEditorProxy) ||
        extractLayoutProxyFromEditorDraft(siteData.LayoutProxyJson)
    );
};

/** Authoritative layout geometry from API (LayoutProxyJson / layoutEditorProxy / EditorDraftJson). */
export const resolveServerLayoutProxyForSite3d = (site) => {
    const fromFetch = extractLayoutProxyFromSiteFetch(site?.data);
    const fromPersisted = site?.persistedLayoutProxyFromEditor;
    const fromDraft = extractLayoutProxyFromEditorDraft(
        site?.siteLayoutEditorDraftRaw
    );

    // fetchUserSite LayoutProxyJson is canonical after Apply to Site 3D; editor-draft
    // layoutProxy in the browser is often stale (pre-rotate) and must not win on clients.
    if (fromFetch?.layoutBins?.length) {
        if (fromPersisted?.layoutBins?.length) {
            const drift = maxLayoutProxyBinDriftFt(fromFetch, fromPersisted);
            if (drift > 1) return fromFetch;
        }
        if (fromDraft?.layoutBins?.length) {
            const drift = maxLayoutProxyBinDriftFt(fromFetch, fromDraft);
            if (drift > 1) return fromFetch;
        }
        return fromFetch;
    }

    if (fromPersisted?.layoutBins?.length) return fromPersisted;
    if (fromDraft?.layoutBins?.length) return fromDraft;

    return null;
};

/** Editor-applied Site 3D (+Z axes) — legacy saved camera/site offsets are invalid. */
export const siteUsesEditorSite3dLayout = (site) =>
    !!(
        site?.layoutUsesEditorLayoutAxes ||
        site?.hasCommittedSite3dLayoutProxy
    );

/** Max |Δx| or |Δz| (ft) between two proxies matched by binId/binName. */
export const maxLayoutProxyBinDriftFt = (proxyA, proxyB) => {
    if (!proxyA?.layoutBins?.length || !proxyB?.layoutBins?.length) return 0;

    const indexB = new Map();
    proxyB.layoutBins.forEach((lb) => {
        layoutBinMatchKeys(lb).forEach((key) => indexB.set(key, lb));
    });

    let maxDrift = 0;
    proxyA.layoutBins.forEach((la) => {
        let lb = null;
        for (const key of layoutBinMatchKeys(la)) {
            lb = indexB.get(key);
            if (lb) break;
        }
        if (!lb) return;
        maxDrift = Math.max(
            maxDrift,
            Math.abs((la.x ?? 0) - (lb.x ?? 0)),
            Math.abs((la.z ?? 0) - (lb.z ?? 0))
        );
    });
    return maxDrift;
};

/** Push exact layoutProxy to LayoutProxyJson so all users load the same geometry. */
export const publishLayoutProxyToServer = async (siteGuid, proxy) => {
    if (!siteGuid || !proxy?.layoutBins?.length) return false;
    const payload = layoutProxyPayloadFromProxy(proxy);
    if (!payload) return false;
    try {
        const ok = await createApiEndpoint().saveCustomerSiteLayout(
            "direct",
            siteGuid,
            {
                bounds: proxy.layout ?? {},
                groups: [],
                layoutProxyJson: payload,
            }
        );
        return ok === true;
    } catch {
        return false;
    }
};

/**
 * Resolve layout proxy: server first, then browser cache from last Apply on this machine.
 * When cache disagrees with the server, prefer cache and publish it to LayoutProxyJson.
 */
export const resolveLayoutProxyForSite3d = (site, options = {}) => {
    const syncStaleServer = options.syncStaleServer !== false;
    const serverProxy = resolveServerLayoutProxyForSite3d(site);
    const cached = loadLayoutProxyForSite3d(site?.siteGuid);

    if (serverProxy?.layoutBins?.length && cached?.layoutBins?.length) {
        const drift = maxLayoutProxyBinDriftFt(serverProxy, cached);
        if (drift > 1) {
            if (syncStaleServer && site?.siteGuid) {
                publishLayoutProxyToServer(site.siteGuid, cached).catch(() => {});
            }
            return cached;
        }
    }

    if (serverProxy?.layoutBins?.length) return serverProxy;

    if (cached?.layoutBins?.length) {
        if (syncStaleServer && site?.siteGuid) {
            publishLayoutProxyToServer(site.siteGuid, cached).catch(() => {});
        }
        return cached;
    }

    return null;
};

export const clearLayoutProxyStorageForSite3d = (siteGuid) => {
    if (!siteGuid) return;
    const draftKey = LAYOUT_DRAFT_SITE3D_KEY(siteGuid);
    const proxyKey = LAYOUT_PROXY_SITE3D_KEY(siteGuid);
    try {
        sessionStorage.removeItem(draftKey);
        sessionStorage.removeItem(proxyKey);
        localStorage.removeItem(draftKey);
        localStorage.removeItem(proxyKey);
    } catch {
        /* ignore */
    }
};

/** GraphQL may return EditorDraftJson as an object or a JSON string (sometimes double-encoded). */
export const parseSiteLayoutEditorDraftFromApi = (raw) => {
    if (raw == null) return null;
    if (typeof raw === "object" && !Array.isArray(raw)) return raw;
    if (typeof raw !== "string") return null;
    try {
        let parsed = JSON.parse(raw.trim());
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
            ? parsed
            : null;
    } catch {
        return null;
    }
};

/** Canonical layoutProxy blob for EditorDraftJson / Site 3D load. */
export const layoutProxyPayloadFromProxy = (proxy) => {
    if (!proxy?.layoutBins?.length) return null;
    return {
        layout: proxy.layout,
        layoutGroups: proxy.layoutGroups,
        layoutBins: proxy.layoutBins,
        layoutSides: proxy.layoutSides,
    };
};

/** Keep published Site 3D geometry when saving editor-only draft (Save must not wipe layoutProxy). */
export const mergeLayoutProxyIntoEditorPayload = (editor, proxy) => {
    const payload = layoutProxyPayloadFromProxy(proxy);
    if (!payload || !editor) return editor;
    return { ...editor, layoutProxy: payload };
};

/** Exact layout-table proxy stored in EditorDraftJson after Apply to Site 3D. */
export const extractLayoutProxyFromEditorDraft = (raw) => {
    const parsed = parseSiteLayoutEditorDraftFromApi(raw);
    if (!parsed || typeof parsed !== "object") return null;
    raw = parsed;
    if (raw.layoutBins?.length) {
        return raw;
    }
    const inner =
        raw.editor && typeof raw.editor === "object" && !Array.isArray(raw.editor)
            ? raw.editor
            : raw;
    const proxy = inner.layoutProxy ?? raw.layoutProxy;
    return proxy?.layoutBins?.length ? proxy : null;
};

/** DB / session may store editor fields flat or under `editor`. */
export const editorDraftPayloadIsValid = (raw) => {
    if (!raw || typeof raw !== "object") return false;
    const inner =
        raw.editor && typeof raw.editor === "object" && !Array.isArray(raw.editor)
            ? raw.editor
            : raw;
    return !!(
        inner.imageDataUrl ||
        extractLayoutProxyFromEditorDraft(raw) ||
        (inner.markers?.length > 0) ||
        (inner.layoutSegments?.length > 0) ||
        (inner.stices?.length > 0)
    );
};

/**
 * Temputech manual round-field layout (e.g. ADM Memphis): production Site 3D uses -layoutZ and
 * persisted layoutCables as stored. EditorDraftJson may exist from opening Site Layout — that
 * must not switch this site to the Preview (+Z / cable-remap) loader.
 */
export const siteLayoutLooksLegacyManual = (site) => {
    if (siteHasCommittedSite3dLayoutProxy(site)) return false;

    const bins = site?.layoutBins || [];
    if (bins.length < 8) return false;

    const roundBins = bins.filter((lb) => lb.isRound);
    const flatBins = bins.filter((lb) => !lb.isRound);
    if (roundBins.length < bins.length * 0.85) return false;

    const sides = site?.layoutSides || [];
    const arcSides = sides.filter((s) => (s.curveRadius ?? 0) > 0).length;
    if (flatBins.length > 2 && arcSides > 8) return false;

    const roundZ = roundBins.map((lb) => lb.z ?? 0).filter(Number.isFinite);
    const negZCount = roundZ.filter((z) => z < -0.5).length;
    if (negZCount >= roundZ.length * 0.2) return true;

    const posZCount = roundZ.filter((z) => z > 0.5).length;
    // Site Layout Apply stores +Z (canvas-down feet). Legacy Temputech manual rows use -Z.
    if (posZCount >= roundZ.length * 0.5) return false;

    if (roundBins.length >= 12 && flatBins.length <= 1) return true;

    return false;
};

/** Layout tables committed this session from Site Layout editor Save. */
export const layoutTablesSavedFromSiteEditor = (site) =>
    !!(site?.layoutSavedFromSiteLayoutEditor || site?.layoutUsesEditorLayoutAxes);

/** Site has editor snapshot and persisted layout tables (reload after Save layout). */
export const siteHasEditorLayoutCommit = (site) =>
    !!(
        site?.hasPersistedSiteLayout &&
        (layoutTablesSavedFromSiteEditor(site) || site?.hasEditorLayoutDraft)
    );

/**
 * Layout table rows match editor-style geometry (Test Collins stices, placeholders, etc.).
 */
export const siteLayoutLikelyFromEditor = (site) => {
    if (siteHasEditorLayoutCommit(site)) return true;
    if (layoutTablesSavedFromSiteEditor(site)) return true;
    if (siteHasCommittedSite3dLayoutProxy(site)) return true;
    if (extractLayoutProxyFromSiteFetch(site?.data)?.layoutBins?.length) {
        return true;
    }
    const roundBins = (site?.layoutBins || []).filter((lb) => lb.isRound);
    if (roundBins.length >= 3) {
        const posZ = roundBins.filter((lb) => (lb.z ?? 0) > 0.5).length;
        if (posZ >= roundBins.length * 0.4) return true;
    }
    if ((site?.layoutBins || []).some((lb) => isLayoutPlaceholderBinId(lb.binId))) {
        return true;
    }
    if ((site?.layoutBins || []).some((lb) => !lb.isRound && (lb.z ?? 0) > 0)) {
        return true;
    }
    const flatBins = (site?.layoutBins || []).filter((lb) => !lb.isRound);
    const sides = site?.layoutSides || [];
    if (!flatBins.length || !sides.length) return false;
    if (sides.some((s) => (s.curveRadius ?? 0) > 0)) return true;
    const sidesPerFlat = sides.length / flatBins.length;
    return sidesPerFlat > 4.5;
};

const layoutSideToDraftLocal = (side, layoutBin) => {
    const row = {
        x1: side.x1,
        y1: side.y1 ?? 0,
        z1: side.z1,
        x2: side.x2,
        y2: side.y2 ?? 0,
        z2: side.z2,
        curveX: side.curveX ?? 0,
        curveY: side.curveY ?? 0,
        curveZ: side.curveZ ?? 0,
        curveRadius: side.curveRadius ?? 0,
        isInner: !!side.isInner,
        isSiteRender: side.isSiteRender !== false,
    };
    if (layoutSideLooksBinLocal(side, layoutBin)) {
        return row;
    }
    return {
        ...row,
        x1: round1((side.x1 ?? 0) - (layoutBin.x ?? 0)),
        z1: round1((side.z1 ?? 0) - (layoutBin.z ?? 0)),
        x2: round1((side.x2 ?? 0) - (layoutBin.x ?? 0)),
        z2: round1((side.z2 ?? 0) - (layoutBin.z ?? 0)),
        curveX: round1((side.curveX ?? 0) - (layoutBin.x ?? 0)),
        curveZ: round1((side.curveZ ?? 0) - (layoutBin.z ?? 0)),
    };
};

/** layoutSide row → draft side (group-relative absolute feet, same as drawSite). */
const layoutSideToDraftSide = (side) => ({
    x1: side.x1,
    y1: side.y1 ?? 0,
    z1: side.z1,
    x2: side.x2,
    y2: side.y2 ?? 0,
    z2: side.z2,
    curveX: side.curveX ?? 0,
    curveY: side.curveY ?? 0,
    curveZ: side.curveZ ?? 0,
    curveRadius: side.curveRadius ?? 0,
    isInner: !!side.isInner,
    isSiteRender: side.isSiteRender !== false,
});

/**
 * Rebuild editor draft from fetchUserSite layout* (same geometry Preview would proxy).
 * @param {object} site
 * @param {{ sidesAreGroupAbsolute?: boolean }} [options]
 *   Editor-saved rows store group-relative absolute feet (save payload contract).
 */
export const buildDraftFromFetchedLayoutTables = (site, options = {}) => {
    if (!site?.layoutGroups?.length || !site?.layoutBins?.length) {
        return null;
    }

    const sidesAreGroupAbsolute =
        options.sidesAreGroupAbsolute === true ||
        site?.hasEditorLayoutDraft ||
        siteLayoutLikelyFromEditor(site);

    const sidesByBinId = new Map();
    (site.layoutSides || []).forEach((side) => {
        const binId = side.customerSiteLayoutBinId;
        if (!sidesByBinId.has(binId)) sidesByBinId.set(binId, []);
        sidesByBinId.get(binId).push(side);
    });

    const groups = site.layoutGroups.map((g) => ({
        groupName: g.groupName ?? "Group 1",
        width: g.width ?? 0,
        length: g.length ?? 0,
        height: g.height ?? 0,
        rotation: g.rotation ?? 0,
        x: g.x ?? 0,
        y: g.y ?? 0,
        z: g.z ?? 0,
        cx: g.cx ?? null,
        cy: g.cy ?? null,
        cz: g.cz ?? null,
        bins: (site.layoutBins || [])
            .filter(
                (lb) =>
                    lb.customerSiteLayoutGroupId ===
                    g.customerSiteLayoutGroupId
            )
            .map((lb) => ({
                binId: lb.binId,
                binName: lb.binName ?? "",
                groupOrder: lb.groupOrder,
                width: lb.width ?? 0,
                length: lb.length ?? 0,
                height: lb.height ?? 0,
                rotation: lb.rotation ?? 0,
                isRound: !!lb.isRound,
                x: lb.x ?? 0,
                y: lb.y ?? 0,
                z: lb.z ?? 0,
                sides: (sidesByBinId.get(lb.customerSiteLayoutBinId) || []).map(
                    (s) => {
                        if (sidesAreGroupAbsolute) {
                            return layoutSideToDraftSide(s);
                        }
                        return layoutSideToDraftLocal(s, lb);
                    }
                ),
            })),
    }));

    const draft = { bounds: site.layout ?? {}, groups };
    if (sidesAreGroupAbsolute) {
        draft.sidesAreGroupAbsolute = true;
    }
    return draft;
};

/**
 * After fetch: run the same side pipeline as Preview so drawSite() gets correct lx/hlx.
 */
/**
 * After drawSite: bin-local footprint polygon for flat stices (Preview 3D path).
 * Avoids production SticeArcWall using broken hlx corner math on editor arcs.
 */
const sideLocalCoordFinite = (n) => typeof n === "number" && Number.isFinite(n);

const BIN_VIEW_CABLE_SHIFT_KEYS = [
    "x",
    "z",
    "topPointX",
    "topPointZ",
    "bottomPointX",
    "bottomPointZ",
    "curveX",
    "curveZ",
];

/** Keep cable/sensor XZ in sync when /bin recenters the wall footprint at origin. */
const shiftLayoutCablePositions = (cable, dx, dz) => {
    if (Math.abs(dx) < 0.005 && Math.abs(dz) < 0.005) return cable;
    const out = { ...cable };
    BIN_VIEW_CABLE_SHIFT_KEYS.forEach((k) => {
        if (!Number.isFinite(out[k])) return;
        out[k] += k === "x" || k.includes("X") ? dx : dz;
    });
    return out;
};

const shiftLayoutSensorPositions = (sensor, dx, dz) => {
    if (Math.abs(dx) < 0.005 && Math.abs(dz) < 0.005) return sensor;
    const out = { ...sensor };
    if (Number.isFinite(out.x)) out.x += dx;
    if (Number.isFinite(out.z)) out.z += dz;
    return out;
};

/**
 * A-frame cables: Temputech layout rows often share stale XZ; BTS positionX/Y/Z is authoritative.
 * Site3D matched this via CustomerSite.js — bin view must do the same before footprint centering.
 */
export const syncAframeLayoutSensorPositionsFromBts = (site) => {
    if (!site?.layoutCables?.length) return site;

    const cables = site.cables ?? [];
    const aframes = site.aframes ?? [];

    site.layoutCables.forEach((layoutCable) => {
        const cable = cables.find((c) => Number(c.id) === Number(layoutCable.cableId));
        if (!cable?.aframeId) return;

        layoutCable.aframe =
            aframes.find((a) => Number(a.id) === Number(cable.aframeId)) ??
            layoutCable.aframe;

        if (Number.isFinite(Number(cable.topPointX))) {
            const x = Number(cable.topPointX);
            layoutCable.topPointX = x;
            layoutCable.x = x;
            layoutCable.bottomPointX = x;
        }
        if (Number.isFinite(Number(cable.topPointZ))) {
            const z = Number(cable.topPointZ);
            layoutCable.topPointZ = z;
            layoutCable.z = z;
            layoutCable.bottomPointZ = z;
        }

        (cable.sensorList ?? []).forEach((sensor) => {
            if (isHumiditySensor(sensor)) return;
            const layoutSensor = (site.layoutSensors ?? []).find(
                (ls) => Number(ls.sensorId) === Number(sensor.id)
            );
            if (!layoutSensor) return;
            if (String(layoutSensor.type ?? "").toLowerCase() === "humidity") return;
            if (Number.isFinite(Number(sensor.positionX)))
                layoutSensor.x = Number(sensor.positionX);
            if (Number.isFinite(Number(sensor.positionY)))
                layoutSensor.y = Number(sensor.positionY) + 4;
            if (Number.isFinite(Number(sensor.positionZ)))
                layoutSensor.z = Number(sensor.positionZ);
        });
    });

    return site;
};

const sideHasDrawSiteLocalEndpoints = (s) =>
    sideLocalCoordFinite(s?.lx1) &&
    sideLocalCoordFinite(s?.lz1) &&
    sideLocalCoordFinite(s?.lx2) &&
    sideLocalCoordFinite(s?.lz2);

/**
 * Footprint from chained walls, but only when every wall made it into the loop.
 * chainLayoutSidesLocal stops at the first wall it cannot join and returns the partial
 * run, which drops corners — a four wall flat house then renders as a triangle.
 */
const closedPolyFtFromLayoutSides = (sides) => {
    const ordered = chainLayoutSidesLocal(sides, chainTolForLayoutSideWorld(sides));
    if (ordered.length < sides.length) return null;
    const poly = polyFtFromOrderedSides(ordered);
    return poly?.length >= sides.length ? poly : null;
};

/** Bin-local footprint from layoutSides (drawSite lx/lz or group-absolute x/z). */
export const buildBinLocalPolygonFromLayoutSides = (layoutBin, sides) => {
    if (!layoutBin || !sides?.length || sides.length < 3) return null;

    // Every wall must carry drawSite locals; chaining a mix of lx/lz and undefined
    // breaks the loop at the first gap, so fall back to the world coords for all walls.
    if (sides.every(sideHasDrawSiteLocalEndpoints)) {
        return closedPolyFtFromLayoutSides(sides);
    }

    const bx = layoutBin.x ?? 0;
    const bz = layoutBin.z ?? 0;
    const local = sides.map((s) => ({
        ...s,
        lx1: (s.x1 ?? 0) - bx,
        lz1: (s.z1 ?? 0) - bz,
        lx2: (s.x2 ?? 0) - bx,
        lz2: (s.z2 ?? 0) - bz,
    }));
    return closedPolyFtFromLayoutSides(local);
};

/** /bin from site card: center the focused bin's wall footprint at the scene origin. */
export const normalizeFocusedBinFootprintForBinView = (site) => {
    const sel = site?.selectedBinId;
    if (sel === undefined || sel === null || sel === 0 || sel === "0") return site;

    const layoutBin = (site.layoutBins || []).find(
        (lb) => sameBinDataId(lb.binId, sel) || sameBinDataId(lb.binDataId, sel)
    );
    if (!layoutBin) return site;

    const sides = (site.layoutSides || []).filter(
        (s) => s.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId
    );

    if (!(layoutBin.previewPolygonFt?.length >= 3) && sides.length >= 3) {
        const poly = buildBinLocalPolygonFromLayoutSides(layoutBin, sides);
        if (poly?.length >= 3) {
            layoutBin.previewPolygonFt = poly;
            layoutBin.loopClosed = true;
        }
    }

    let centerX = 0;
    let centerZ = 0;
    let didCenter = false;

    if (layoutBin.previewPolygonFt?.length >= 3) {
        const poly = layoutBin.previewPolygonFt;
        centerX = poly.reduce((a, p) => a + p.x, 0) / poly.length;
        centerZ = poly.reduce((a, p) => a + p.z, 0) / poly.length;
        layoutBin.previewPolygonFt = centerPolygonFtAtOrigin(poly);
        didCenter = true;
    }

    if (sides.length) {
        const pts = [];
        sides.forEach((s) => {
            if (sideLocalCoordFinite(s.lx1) && sideLocalCoordFinite(s.lz1)) {
                pts.push({ x: s.lx1, z: s.lz1 });
            }
            if (sideLocalCoordFinite(s.lx2) && sideLocalCoordFinite(s.lz2)) {
                pts.push({ x: s.lx2, z: s.lz2 });
            }
        });
        if (pts.length) {
            if (!didCenter) {
                centerX = pts.reduce((a, p) => a + p.x, 0) / pts.length;
                centerZ = pts.reduce((a, p) => a + p.z, 0) / pts.length;
            }
            sides.forEach((s) => {
                ["lx1", "lx2", "hlx1", "hlx2", "lcurveX"].forEach((k) => {
                    if (sideLocalCoordFinite(s[k])) s[k] -= centerX;
                });
                ["lz1", "lz2", "hlz1", "hlz2", "lcurveZ"].forEach((k) => {
                    if (sideLocalCoordFinite(s[k])) s[k] -= centerZ;
                });
            });
        }
    }

    if (Math.abs(centerX) > 0.005 || Math.abs(centerZ) > 0.005) {
        layoutBin.footprintCenterOffsetFt = { x: centerX, z: centerZ };
        const layoutBinId = layoutBin.customerSiteLayoutBinId;
        site.layoutCables = (site.layoutCables || []).map((lc) =>
            lc.customerSiteLayoutBinId === layoutBinId
                ? shiftLayoutCablePositions(lc, -centerX, -centerZ)
                : lc
        );
        site.layoutSensors = (site.layoutSensors || []).map((ls) =>
            ls.customerSiteLayoutBinId === layoutBinId
                ? shiftLayoutSensorPositions(ls, -centerX, -centerZ)
                : ls
        );
    }

    layoutBin.x = 0;
    layoutBin.z = 0;
    return site;
};

export const attachPreviewPolygonsFromLayoutSides = (site) => {
    if (!site?.layoutBins?.length || !site?.layoutSides?.length) return site;

    site.layoutBins.forEach((lb) => {
        // Round silos keep their circular footprint. Connected silos pick up perimeter wall
        // sides from neighbors (augmentDraftWithRoundBinPerimeterSides), but those walls must
        // not turn the silo into a flat polygon — that's the Site 3D vs Preview mismatch.
        if (lb.isRound) return;
        if (lb.previewPolygonFt?.length >= 3) return;
        const sides = site.layoutSides.filter(
            (s) => s.customerSiteLayoutBinId === lb.customerSiteLayoutBinId
        );
        if (sides.length < 3) return;
        const poly = buildBinLocalPolygonFromLayoutSides(lb, sides);
        if (poly?.length >= 3) {
            lb.previewPolygonFt = poly;
            lb.loopClosed = true;
        }
    });
    return site;
};

/** Dev: log drift between fetchUserSite rows and editor Save proxy (not a lossy round-trip). */
export const logLayoutProxyRoundTripDrift = (site, label = "layout", fetchSnap) => {
    if (process.env.NODE_ENV === "production") return;

    const storedProxy = loadLayoutProxyForSite3d(site.siteGuid);
    const editorProxy =
        storedProxy?.layoutBins?.length
            ? storedProxy
            : buildLayoutTableProxyFromEditorDraft(
                  buildDraftFromFetchedLayoutTables(site, {
                      sidesAreGroupAbsolute: true,
                  }),
                  site.siteGuid
              );
    if (!editorProxy?.layoutBins?.length) return;

    const fetchedBins = fetchSnap?.layoutBins ?? site.layoutBins ?? [];
    const fetchedSides = fetchSnap?.layoutSides ?? site.layoutSides ?? [];

    let maxSideDrift = 0;
    const fetchedSideByBin = new Map();
    fetchedSides.forEach((s) => {
        const id = s.customerSiteLayoutBinId;
        if (!fetchedSideByBin.has(id)) fetchedSideByBin.set(id, []);
        fetchedSideByBin.get(id).push(s);
    });
    (editorProxy.layoutSides || []).forEach((ps) => {
        const matches = fetchedSideByBin.get(ps.customerSiteLayoutBinId) || [];
        const fs = matches.find(
            (s) =>
                Math.abs((s.x1 ?? 0) - (ps.x1 ?? 0)) < 2 &&
                Math.abs((s.z1 ?? 0) - (ps.z1 ?? 0)) < 2
        );
        if (!fs) return;
        ["x1", "z1", "x2", "z2"].forEach((k) => {
            const d = Math.abs((fs[k] ?? 0) - (ps[k] ?? 0));
            if (d > maxSideDrift) maxSideDrift = d;
        });
    });

    let maxBinDrift = 0;
    const fetchedBinById = new Map(
        fetchedBins.map((lb) => [lb.customerSiteLayoutBinId, lb])
    );
    editorProxy.layoutBins.forEach((pb) => {
        const fb = fetchedBinById.get(pb.customerSiteLayoutBinId);
        if (!fb) return;
        const dx = Math.abs((fb.x ?? 0) - (pb.x ?? 0));
        const dz = Math.abs((fb.z ?? 0) - (pb.z ?? 0));
        maxBinDrift = Math.max(maxBinDrift, dx, dz);
    });

    const maxDrift = Math.max(maxSideDrift, maxBinDrift);
    if (maxDrift > 2) {
        const hasStored = !!storedProxy?.layoutBins?.length;
        console.warn(
            `[${label}] fetchUserSite vs editor proxy drift: ${round1(maxDrift)} ft` +
                (hasStored
                    ? " — Site 3D uses last Save layout from browser storage; fix API save/read to clear this"
                    : " — Save layout on this browser, then reopen Site 3D")
        );
    }
};

export const rehydrateLayoutGeometryForDrawSite = (site) => {
    if (!site?.layoutBins?.length) return site;

    const priorBins = new Map(
        (site.layoutBins || []).map((lb) => [lb.customerSiteLayoutBinId, lb])
    );

    const draft = buildDraftFromFetchedLayoutTables(site, {
        sidesAreGroupAbsolute:
            site?.hasPersistedSiteLayout ||
            siteLayoutLikelyFromEditor(site),
    });
    if (!draft?.groups?.length) return site;

    const proxy = buildLayoutTableProxyFromEditorDraft(draft, site.siteGuid);
    if (!proxy) return site;

    site.layout = proxy.layout ?? site.layout;
    site.layoutGroups = proxy.layoutGroups ?? site.layoutGroups;
    site.layoutBins = (proxy.layoutBins ?? []).map((lb) => {
        const prior = priorBins.get(lb.customerSiteLayoutBinId);
        return {
            ...lb,
            bin: prior?.bin || binRecordForLayoutBin(site, lb),
        };
    });
    site.layoutSides = proxy.layoutSides ?? site.layoutSides;
    return site;
};

/**
 * Editor draft bins carry stice-local sides; layout tables + drawSite() need
 * group-relative absolute feet (same as manual layout rows).
 */
export const prepareDraftForLayoutTableSave = (draft) => {
    if (!draft?.groups?.length) return draft;
    draft.groups.forEach((g) => {
        (g.bins || []).forEach((b) => {
            if (b.isRound || !(b.sides || []).length) return;
            const bx = b.x ?? 0;
            const bz = b.z ?? 0;
            b.sides = b.sides.map((s) => {
                if (!layoutSideLooksBinLocal(s, b)) return { ...s };
                return {
                    ...s,
                    x1: round1(bx + (s.x1 ?? 0)),
                    z1: round1(bz + (s.z1 ?? 0)),
                    x2: round1(bx + (s.x2 ?? 0)),
                    z2: round1(bz + (s.z2 ?? 0)),
                    curveX: round1(bx + (s.curveX ?? 0)),
                    curveZ: round1(bz + (s.curveZ ?? 0)),
                };
            });
        });
    });
    return draft;
};

/** Match a BTS bin to its round layout row (never flat/stice pockets). */
export const findRoundLayoutBinForSiteBin = (site, siteBin) => {
    if (!site?.layoutBins?.length || !siteBin) return null;
    return (
        site.layoutBins.find(
            (lb) =>
                lb.isRound &&
                !isLayoutPlaceholderBinId(lb.binId) &&
                (sameBinDataId(lb.binId, siteBin.binDataId) ||
                    (lb.binName != null && lb.binName === siteBin.binName))
        ) ?? null
    );
};

export const sideToGroupAbsolute = (side, layoutBin) => {
    const bx = layoutBin?.x || 0;
    const bz = layoutBin?.z || 0;
    if (!layoutSideLooksBinLocal(side, layoutBin)) {
        return { ...side };
    }
    return {
        ...side,
        x1: bx + (side.x1 ?? 0),
        y1: side.y1 ?? 0,
        z1: bz + (side.z1 ?? 0),
        x2: bx + (side.x2 ?? 0),
        y2: side.y2 ?? 0,
        z2: bz + (side.z2 ?? 0),
        curveX: bx + (side.curveX ?? 0),
        curveY: side.curveY ?? 0,
        curveZ: bz + (side.curveZ ?? 0),
    };
};

const binRecordForLayoutBin = (site, layoutBin) => {
    const live = findSiteBinForLayoutBin(site, layoutBin);
    if (live) {
        if (layoutBin.isRound && layoutBin.width > 0) {
            return {
                ...live,
                width: layoutBin.width,
                length:
                    layoutBin.length > 0
                        ? layoutBin.length
                        : layoutBin.width,
                diameter: layoutBin.width,
            };
        }
        return live;
    }
    return {
        binDataId: layoutBin.binId,
        binId: layoutBin.binId,
        binName: layoutBin.binName,
        type: layoutBin.isRound ? "Round" : "Flat",
        height: layoutBin.height || 30,
        width: layoutBin.width,
        length: layoutBin.length,
        diameter: layoutBin.isRound ? layoutBin.width : undefined,
        degreesToNorth: layoutBin.rotation || 0,
    };
};

/**
 * Build layout-table-shaped JSON from editor draft (saveCustomerSiteLayout groups shape in,
 * fetchUserSite layout* shape out).
 * @param {object} draft — { bounds, groups: [{ bins: [{ sides?, x, z, ... }] }] }
 * @param {string} [siteGuid]
 */
export const buildLayoutTableProxyFromEditorDraft = (draft, siteGuid) => {
    if (!draft?.groups?.length) return null;

    const sidesAreGroupAbsolute = draft.sidesAreGroupAbsolute === true;

    let layoutGroupId = 0;
    let layoutBinId = 0;
    let layoutSideId = 0;

    const layoutGroups = [];
    const layoutBins = [];
    const layoutSides = [];

    draft.groups.forEach((g) => {
        const groupRow = {
            customerSiteLayoutGroupId: ++layoutGroupId,
            customerSiteLayoutId: 1,
            groupName: g.groupName || `Group ${layoutGroupId}`,
            width: g.width ?? 0,
            length: g.length ?? 0,
            height: g.height ?? 0,
            rotation: g.rotation ?? 0,
            x: g.x ?? 0,
            y: g.y ?? 0,
            z: g.z ?? 0,
            cx: g.cx ?? null,
            cy: g.cy ?? null,
            cz: g.cz ?? null,
        };
        layoutGroups.push(groupRow);

        (g.bins || []).forEach((b) => {
            const layoutBin = {
                customerSiteLayoutBinId: ++layoutBinId,
                customerSiteLayoutId: 1,
                customerSiteLayoutGroupId: groupRow.customerSiteLayoutGroupId,
                groupOrder: b.groupOrder ?? layoutBinId,
                binId: b.binId,
                binName: b.binName ?? "",
                width: b.width ?? 0,
                length: b.length ?? 0,
                height: b.height ?? 0,
                rotation: b.rotation ?? 0,
                isRound: !!b.isRound,
                x: b.x ?? 0,
                y: b.y ?? 0,
                z: b.z ?? 0,
                cx: b.cx ?? null,
                cy: b.cy ?? null,
                cz: b.cz ?? null,
            };
            if (b.previewPolygonFt?.length >= 3) {
                layoutBin.previewPolygonFt = b.previewPolygonFt;
            }
            if (b.loopClosed != null) {
                layoutBin.loopClosed = b.loopClosed;
            }

            layoutBins.push(layoutBin);

            (b.sides || []).forEach((s) => {
                const abs = sidesAreGroupAbsolute
                    ? { ...s }
                    : sideToGroupAbsolute(s, layoutBin);
                layoutSides.push({
                    customerSiteLayoutSideId: ++layoutSideId,
                    customerSiteLayoutId: 1,
                    customerSiteLayoutGroupId: groupRow.customerSiteLayoutGroupId,
                    customerSiteLayoutBinId: layoutBin.customerSiteLayoutBinId,
                    binId: b.binId,
                    binName: b.binName ?? "",
                    x1: abs.x1,
                    y1: abs.y1 ?? 0,
                    z1: abs.z1,
                    x2: abs.x2,
                    y2: abs.y2 ?? 0,
                    z2: abs.z2,
                    curveX: abs.curveX ?? 0,
                    curveY: abs.curveY ?? 0,
                    curveZ: abs.curveZ ?? 0,
                    curveRadius: s.curveRadius ?? 0,
                    isInner: !!s.isInner,
                    isSiteRender: s.isSiteRender !== false,
                });
            });
        });
    });

    const bounds = draft.bounds || {
        width: 0,
        length: 0,
        height: 0,
        rotation: 0,
        x: 0,
        y: 0,
        z: 0,
        cx: null,
        cy: null,
        cz: null,
        lx: null,
        ly: null,
        lz: null,
    };

    const proxy = {
        layout: {
            customerSiteLayoutId: 1,
            customerSiteGuid: siteGuid ?? null,
            width: bounds.width ?? 0,
            length: bounds.length ?? 0,
            height: bounds.height ?? 0,
            rotation: bounds.rotation ?? 0,
            x: bounds.x ?? 0,
            y: bounds.y ?? 0,
            z: bounds.z ?? 0,
            cx: bounds.cx ?? null,
            cy: bounds.cy ?? null,
            cz: bounds.cz ?? null,
            lx: bounds.lx ?? null,
            ly: bounds.ly ?? null,
            lz: bounds.lz ?? null,
        },
        layoutGroups,
        layoutBins,
        layoutSides,
        layoutCables: [],
        layoutSensors: [],
    };

    finalizeLayoutSidesForDrawSite(proxy);
    return proxy;
};

/**
 * Per-bin perimeter order for drawSite() corner math (same expectation as persisted layout rows).
 * Only reorders when endpoint chaining is incomplete — avoids reversing arcs on an already-good loop.
 */
export const finalizeLayoutSidesForDrawSite = (proxy) => {
    if (!proxy?.layoutSides?.length || !proxy?.layoutBins?.length) return proxy;

    const byBinId = new Map();
    proxy.layoutSides.forEach((side) => {
        const binId = side.customerSiteLayoutBinId;
        if (!byBinId.has(binId)) byBinId.set(binId, []);
        byBinId.get(binId).push(side);
    });

    const orderedSides = [];
    proxy.layoutBins
        .slice()
        .sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0))
        .forEach((bin) => {
            const binSides = byBinId.get(bin.customerSiteLayoutBinId) || [];
            if (binSides.length < 2) {
                orderedSides.push(...binSides);
                return;
            }
            const tol = chainTolForLayoutSideWorld(binSides);
            const chained = orderLayoutSideRefsByEndpoints(binSides, tol);
            orderedSides.push(
                ...(chained.length >= binSides.length ? chained : binSides)
            );
        });

    proxy.layoutSides = orderedSides;
    return proxy;
};

const round1 = (v) => Math.round(v * 10) / 10;

const expandBinFootprint = (ax, az, b, acc) => {
    if (b.previewPolygonFt?.length >= 3) {
        b.previewPolygonFt.forEach((p) => {
            if (!Number.isFinite(p.x) || !Number.isFinite(p.z)) return;
            acc.minX = Math.min(acc.minX, ax + p.x);
            acc.maxX = Math.max(acc.maxX, ax + p.x);
            acc.minZ = Math.min(acc.minZ, az + p.z);
            acc.maxZ = Math.max(acc.maxZ, az + p.z);
        });
        return;
    }
    const hw = (b.width || 0) / 2;
    const hl = (b.length || 0) / 2;
    acc.minX = Math.min(acc.minX, ax - hw);
    acc.maxX = Math.max(acc.maxX, ax + hw);
    acc.minZ = Math.min(acc.minZ, az - hl);
    acc.maxZ = Math.max(acc.maxZ, az + hl);
};

const clearSideDrawSiteFields = (s) => {
    delete s.lx1;
    delete s.lz1;
    delete s.lx2;
    delete s.lz2;
    delete s.lcurveX;
    delete s.lcurveY;
    delete s.lcurveZ;
    delete s.hlx1;
    delete s.hly1;
    delete s.hlz1;
    delete s.hlx2;
    delete s.hly2;
    delete s.hlz2;
    delete s.newCurveRadius;
    delete s.x3;
    delete s.x4;
    delete s.z3;
    delete s.z4;
};

/** Reset saved Site 3D site-view camera/pan after layout publish (stale offsets aim at old geometry). */
export const clearSite3dSavedViewsForSite = async (siteGuid) => {
    if (!siteGuid) return;
    const payload = {
        viewType: "Site",
        binId: 0,
        siteOffsetX: 0,
        siteOffsetY: 0,
        siteOffsetZ: 0,
        lookAtX: 0,
        lookAtY: 0,
        lookAtZ: 0,
        cameraPositionX: 0,
        cameraPositionY: 0,
        cameraPositionZ: 0,
        cameraRotationX: 0,
        cameraRotationY: 0,
        cameraRotationZ: 0,
        polarAngle: null,
        azimuthAngle: null,
        cameraZoom: null,
        cameraDistance: null,
        canDrag: true,
    };
    try {
        await createApiEndpoint().saveUserCustomerSiteSavedViewForAllUsers(
            "direct",
            siteGuid,
            "Site",
            0,
            payload
        );
    } catch (err) {
        console.warn("clearSite3dSavedViewsForSite", err);
    }
};

/**
 * Center all layout objects like initializeSiteLayout (round grid): layout/group at half
 * extents, bins centered with shift, stice side rows keep geometry relative to each bin.
 */
export const repositionLayoutSiteForSite3d = (site) => {
    const groups = site?.layoutGroups ?? [];
    const bins = site?.layoutBins ?? [];
    const sides = site?.layoutSides ?? [];
    if (!bins.length) return site;

    const acc = {
        minX: Infinity,
        maxX: -Infinity,
        minZ: Infinity,
        maxZ: -Infinity,
        maxH: 0,
    };

    const binRows = bins.map((lb) => {
        const g = groups.find(
            (gg) =>
                gg.customerSiteLayoutGroupId === lb.customerSiteLayoutGroupId
        );
        const absX = (g?.x ?? 0) + (lb.x ?? 0);
        const absZ = (g?.z ?? 0) + (lb.z ?? 0);
        expandBinFootprint(absX, absZ, lb, acc);
        acc.maxH = Math.max(acc.maxH, lb.height || 0);
        return { lb, g, absX, absZ };
    });

    sides.forEach((s) => {
        [
            [s.x1, s.z1],
            [s.x2, s.z2],
            [s.curveX, s.curveZ],
        ].forEach(([x, z]) => {
            if (!Number.isFinite(x) || !Number.isFinite(z)) return;
            acc.minX = Math.min(acc.minX, x);
            acc.maxX = Math.max(acc.maxX, x);
            acc.minZ = Math.min(acc.minZ, z);
            acc.maxZ = Math.max(acc.maxZ, z);
        });
    });

    if (!Number.isFinite(acc.minX)) return site;

    const width = round1(acc.maxX - acc.minX);
    const length = round1(acc.maxZ - acc.minZ);
    const centerX = (acc.minX + acc.maxX) / 2;
    const centerZ = (acc.minZ + acc.maxZ) / 2;
    const primaryGroupId =
        groups[0]?.customerSiteLayoutGroupId ?? 1;
    const layoutId =
        groups[0]?.customerSiteLayoutId ??
        site.layout?.customerSiteLayoutId ??
        1;

    // Group at origin so Site 3D scene centroid is near (0,0) without centerSite().
    site.layout = {
        ...(site.layout || {}),
        width,
        length,
        height: round1(acc.maxH),
        x: 0,
        y: 0,
        z: 0,
    };

    site.layoutGroups = [
        {
            customerSiteLayoutGroupId: primaryGroupId,
            customerSiteLayoutId: layoutId,
            groupName: groups[0]?.groupName ?? "Group 1",
            width,
            length,
            height: round1(acc.maxH),
            rotation: groups[0]?.rotation ?? 0,
            x: 0,
            y: 0,
            z: 0,
            cx: null,
            cy: null,
            cz: null,
        },
    ];

    site.layoutBins = binRows.map(({ lb, absX, absZ }, idx) => ({
        ...lb,
        customerSiteLayoutGroupId: primaryGroupId,
        groupOrder: lb.groupOrder ?? idx + 1,
        x: round1(absX - centerX),
        z: round1(absZ - centerZ),
    }));

    if (sides.length) {
        const binById = new Map(
            binRows.map(({ lb }) => [lb.customerSiteLayoutBinId, lb])
        );
        site.layoutSides = sides.map((s) => {
            const lb = binById.get(s.customerSiteLayoutBinId);
            const probe = lb
                ? {
                      ...lb,
                      width: lb.width || 1,
                      length: lb.length || 1,
                  }
                : null;
            const isLocal = probe && layoutSideLooksBinLocal(s, probe);
            const next = {
                ...s,
                customerSiteLayoutGroupId: primaryGroupId,
            };
            if (!isLocal) {
                next.x1 = round1((s.x1 ?? 0) - centerX);
                next.z1 = round1((s.z1 ?? 0) - centerZ);
                next.x2 = round1((s.x2 ?? 0) - centerX);
                next.z2 = round1((s.z2 ?? 0) - centerZ);
                next.curveX = round1((s.curveX ?? 0) - centerX);
                next.curveZ = round1((s.curveZ ?? 0) - centerZ);
            }
            clearSideDrawSiteFields(next);
            return next;
        });
    }

    return site;
};

/** Editor draft → production layout rows before save. */
export const normalizeLayoutDraftForSite3d = (draft) => {
    if (!draft?.groups?.length) return draft;

    const flat = [];
    draft.groups.forEach((g) => {
        (g.bins || []).forEach((b) => {
            flat.push({
                bin: b,
                absX: (g.x || 0) + (b.x || 0),
                absZ: (g.z || 0) + (b.z || 0),
            });
        });
    });
    if (!flat.length) return draft;

    const acc = {
        minX: Infinity,
        maxX: -Infinity,
        minZ: Infinity,
        maxZ: -Infinity,
        maxH: 0,
    };
    flat.forEach(({ bin, absX, absZ }) => {
        expandBinFootprint(absX, absZ, bin, acc);
        acc.maxH = Math.max(acc.maxH, bin.height || 0);
        (bin.sides || []).forEach((s) => {
            [
                [s.x1, s.z1],
                [s.x2, s.z2],
                [s.curveX, s.curveZ],
            ].forEach(([x, z]) => {
                if (!Number.isFinite(x) || !Number.isFinite(z)) return;
                acc.minX = Math.min(acc.minX, absX + x);
                acc.maxX = Math.max(acc.maxX, absX + x);
                acc.minZ = Math.min(acc.minZ, absZ + z);
                acc.maxZ = Math.max(acc.maxZ, absZ + z);
            });
        });
    });
    if (!Number.isFinite(acc.minX)) return draft;

    const width = round1(acc.maxX - acc.minX);
    const length = round1(acc.maxZ - acc.minZ);
    const centerX = (acc.minX + acc.maxX) / 2;
    const centerZ = (acc.minZ + acc.maxZ) / 2;
    const anchorX = width / 2;
    const anchorZ = length / 2;

    draft.bounds = {
        ...(draft.bounds || {}),
        width,
        length,
        height: round1(acc.maxH),
        rotation: draft.bounds?.rotation ?? 0,
        x: anchorX,
        y: 0,
        z: anchorZ,
        cx: round1(centerX),
        cy: 0,
        cz: round1(centerZ),
        lx: draft.bounds?.lx ?? null,
        ly: draft.bounds?.ly ?? null,
        lz: draft.bounds?.lz ?? null,
    };

    const mergedBins = flat
        .sort(
            (a, b) =>
                (a.bin.groupOrder ?? 0) - (b.bin.groupOrder ?? 0) ||
                String(a.bin.binName ?? "").localeCompare(
                    String(b.bin.binName ?? "")
                )
        )
        .map(({ bin, absX, absZ }, idx) => ({
            ...bin,
            groupOrder: bin.groupOrder ?? idx + 1,
            x: round1(absX - centerX),
            z: round1(-(absZ - centerZ)),
        }));

    draft.groups = [
        {
            groupName: draft.groups[0]?.groupName || "Group 1",
            width,
            length,
            height: round1(acc.maxH),
            rotation: 0,
            x: anchorX,
            y: 0,
            z: anchorZ,
            cx: null,
            cy: null,
            cz: null,
            bins: mergedBins,
        },
    ];

    return draft;
};

const layoutBinToDraftBin = (lb, sides) => ({
    binId: lb.binId,
    binName: lb.binName ?? "",
    groupOrder: lb.groupOrder,
    width: lb.width ?? 0,
    length: lb.length ?? 0,
    height: lb.height ?? 0,
    rotation: lb.rotation ?? 0,
    isRound: !!lb.isRound,
    x: lb.x ?? 0,
    y: lb.y ?? 0,
    z: lb.z ?? 0,
    cx: lb.cx ?? null,
    cy: lb.cy ?? null,
    cz: lb.cz ?? null,
    sides: sides || [],
    ...(lb.previewPolygonFt?.length >= 3
        ? { previewPolygonFt: lb.previewPolygonFt }
        : {}),
    ...(lb.loopClosed != null ? { loopClosed: lb.loopClosed } : {}),
});

/**
 * Same geometry Preview renders — persist proxy rows, not a re-normalized draft.
 */
export const buildSavePayloadFromLayoutProxy = (proxy, feetPerPixel) => {
    if (!proxy?.layoutGroups?.length) return null;

    const sidesByBinId = new Map();
    (proxy.layoutSides || []).forEach((side) => {
        const binId = side.customerSiteLayoutBinId;
        if (!sidesByBinId.has(binId)) sidesByBinId.set(binId, []);
        sidesByBinId.get(binId).push(layoutSideToDraftSide(side));
    });

    const groups = proxy.layoutGroups.map((g) => ({
        groupName: g.groupName ?? "Group 1",
        width: g.width ?? 0,
        length: g.length ?? 0,
        height: g.height ?? 0,
        rotation: g.rotation ?? 0,
        x: g.x ?? 0,
        y: g.y ?? 0,
        z: g.z ?? 0,
        cx: g.cx ?? null,
        cy: g.cy ?? null,
        cz: g.cz ?? null,
        bins: (proxy.layoutBins || [])
            .filter(
                (lb) =>
                    lb.customerSiteLayoutGroupId ===
                    g.customerSiteLayoutGroupId
            )
            .map((lb) =>
                layoutBinToDraftBin(
                    lb,
                    sidesByBinId.get(lb.customerSiteLayoutBinId) || []
                )
            ),
    }));

    return {
        bounds: proxy.layout ?? {},
        groups,
        feetPerPixel: feetPerPixel ?? null,
    };
};

/**
 * Payload for saveCustomerSiteLayout (matches Preview proxy geometry).
 */
export const buildSavePayloadFromEditorDraft = (draft, siteGuid) => {
    if (!draft?.groups?.length) return null;
    const proxy = buildLayoutTableProxyFromEditorDraft(draft, siteGuid);
    if (!proxy) return null;
    return buildSavePayloadFromLayoutProxy(proxy, draft.feetPerPixel);
};

/**
 * Apply proxy rows onto selectedSite exactly like fetchUserSite → initializeSiteLayout.
 * @param {object} [options]
 * @param {boolean} [options.basesOnly] — no layout cables/sensors
 * @param {boolean} [options.skipCablePopulate] — do not synthesize cables from BTS cableList
 * @param {boolean} [options.skipDrawSite] — editor layout: use preview polygons, not drawSite hlx
 */
export const applyLayoutTableProxyToSite = (site, proxy, options = {}) => {
    const basesOnly = options.basesOnly === true;
    const skipCablePopulate = options.skipCablePopulate === true;
    const skipDrawSite = options.skipDrawSite === true;
    if (!site || !proxy) return false;

    site.layout = proxy.layout ?? {};
    site.layoutGroups = proxy.layoutGroups ?? [];
    site.layoutBins = (proxy.layoutBins ?? []).map((lb) => ({
        ...lb,
        bin: binRecordForLayoutBin(site, lb),
    }));
    site.layoutSides = proxy.layoutSides ?? [];

    if (basesOnly) {
        site.layoutCables = [];
        site.layoutSensors = [];
    } else {
        site.layoutCables = proxy.layoutCables ?? site.layoutCables ?? [];
        site.layoutSensors = proxy.layoutSensors ?? site.layoutSensors ?? [];
    }

    site.siteRef = [];
    site.groupsRef = [];
    site.binsRef = [];
    site.sensorsGroupsRef = [];

    if (skipDrawSite) {
        site.skipDrawSiteForEditorLayout = true;
    }
    try {
        global.initializeSiteLayout?.();
    } catch {
        /* drawSite runs inside initializeSiteLayout */
    } finally {
        delete site.skipDrawSiteForEditorLayout;
    }

    if (!basesOnly && !skipCablePopulate) {
        global.populateLayoutCablesFromBins?.(site);
    }

    return true;
};
